// Page-observed timed completion. The boundary retires unsupported finishes with a named skip.
import { ecId, ecLean, handBack, insertRoll, itemField, record, Refusal, scalar, upper, type CampaignClock, type JanusCtx, type Row } from './context.js';
import { statusItemId, statusPaused, statusRows } from '../statuses/expiry.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { parseTimedCastStorage } from './timed-cast.js';
import { fabricationAllowsTargetlessCast } from './fabrication.js';
import { isSummonAbility, summonPointTarget, validSummonPoint, SUMMON_POINTS_MAX } from './summon.js';
import { iconPh } from '../actions/spell-components.js';
import { timedFinishGateVerdict } from '../actions/ability-preconditions.js';
import type { AbilityUseReaders } from '../actions/ability-use.js';
import { finalizePlacedAoeTargets, placedAoeSpan, SHAPES } from './aoe/expand-placed-aoe-targets.js';
import { janus, type JanusInput } from './janus.js';
import { runTriggerFamily } from './trigger-executor.js';
import { combatCenter, combatSizeFt } from '../actions/ability-preconditions.js';
import { effectiveSpellRangeId, persistentSpellRangeMultiplier, primaryRangeRefusal, spellRangeMultiplier, throughSummonOrigins } from '../actions/cast-range-gate.js';
import { finishReading, type ReadingFacts } from './timed-finish/reading.js';
import { finishTraining } from './timed-finish/training.js';
import { finishGathering, finishDisenchanting } from './timed-finish/activities.js';
import { hostileSpellImmunity } from './nullify-post-effect-conditions.js';
import { targetImmuneToAbilityId } from './immune-to-ability.js';
import { applyAoeSparesAllies, ctxProviderReaders, spellUncounterable } from './caster-spell-layers.js';
import { ftTimedActivityIsDue } from '../port/k4-dispatch/timed_activity_completion.js';
import { clockPortEnvironment, statusClockRow } from '../port/native-clock.js';
import { upcastAreaGrowthFt } from './upcast.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { resourceReductionReaders } from './resource-reduction.js';
import { resolveConnectionCascade } from '../statuses/gm-boundary.js';

/** The legacy finish's numeric band still uses center distance; reach uses the shared rim distance. Keep this scoped to timed finishes. */
export function timedFinishRangeRefusal(caster: Row, target: Row, ability: Row, read: AbilityUseReaders, roster: readonly Row[]): string | null | undefined {
  const range = effectiveSpellRangeId(caster, ability, read);
  const bands: Record<string, number> = { '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30, 'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60,
    'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120, 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240 };
  if (!range || !bands[range]) return primaryRangeRefusal(caster, target, ability, read, roster);
  const summons = throughSummonOrigins(caster, ability, roster, read, range);
  if (read.has(caster, 'swarm_width_lanes') || read.has(target, 'swarm_width_lanes') || read.has(ability, 'caster_moves_towards_target_in_ft')
      || summons === undefined || summons?.length) return undefined;
  const a = combatCenter(caster), b = combatCenter(target), sa = combatSizeFt(caster, read), sb = combatSizeFt(target, read);
  if (!a || !b || sa === null) return undefined;
  const credit = (s: number | null): number => s === null ? 0 : Math.max(0, (s - 5) / 2);
  const dz = Math.max(0, Math.abs(b.z-a.z) - sa/2 - (sb??0)/2);
  const distance = Math.max(0, Math.hypot(b.x-a.x, b.y-a.y, dz) - credit(sa) - credit(sb));
  const persistent = persistentSpellRangeMultiplier(caster, ability, read);
  if (persistent === undefined) return undefined;
  const allowed = bands[range]! * Math.min(10, persistent * spellRangeMultiplier(caster, ability, read));
  return distance <= allowed ? null : `Target is out of range by ${Math.round((distance-allowed)*10)/10} ft.`;
}

export const TIMED_ACTIVITY_FAMILIES = {
  casting: '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3', casting_ritual_spell: '5833A733-D277-0A60-3A94-A0F355A79A80',
  gathering: '3F171782-CC56-32DF-81B4-8C0ED89F47F4', lockpicking: 'FF9A38A3-5F35-F083-E153-7DF41B0C0826',
  reading: 'ED30CCB0-1645-B97E-B9A6-867D8D0094BD', training: '426C7E0B-EF44-498C-10B2-B50A9E28E45C',
  donning: 'D343892B-338C-4FBE-BEA0-2015FE503F43', doffing: '398DD6E8-AFAE-EA0D-6FE9-C22842D53775',
  disenchanting: '1C4D81BB-FB79-68AD-E881-28D874927FD2',
} as const;
export type TimedActivityFamily = keyof typeof TIMED_ACTIVITY_FAMILIES;
const PARTS = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const truth = (v: unknown): boolean => !(v == null || v === false || v === '' || v === 0 || v === '0' || (Array.isArray(v) && !v.length));
export const timedActivityFamily = (status: Row): TimedActivityFamily | null =>
  (Object.keys(TIMED_ACTIVITY_FAMILIES) as TimedActivityFamily[]).find(k => TIMED_ACTIVITY_FAMILIES[k] === statusItemId(status)) ?? null;

/** compare_campaign_times is lexicographic, inclusive. No conversion to normalized calendar seconds (e.g. day=0 stays day=0). */
export function timedActivityIsDue(status: Row, clock: CampaignClock): boolean {
  if (Number(scalar(status.status_deleted) ?? 0) !== 0) return false;
  const row = statusClockRow(status, scalar);
  return ftTimedActivityIsDue(row, clock, clockPortEnvironment(clock, {
    is_status_effect_permanent: () => !PARTS.some(p => Number(row[`status_ends_campaign_${p}`]) !== 0),
    status_instance_has_campaign_pause: () => statusPaused(status),
  }));
}
/** null means an unheld definition might block; never silently treat it as absence. */
export function timedActivityOwnerBlocked(owner: Row, statusId: string, read?: Pick<AbilityUseReaders, 'definition' | 'value' | 'statuses'>): boolean | null {
  let missing = false;
  for (const s of read?.statuses(owner) ?? statusRows(owner, 'target')) {
    if (upper(s.status_id) === upper(statusId) || Number(scalar(s.status_deleted) ?? 0) !== 0 || statusPaused(s) || Number(scalar(s.status_private) ?? 0) !== 0) continue;
    const held = record(s.status_arr);
    const def = read?.definition(statusItemId(s)) ?? (held && record(held.item_layer_types) ? held : null);
    if (!def) { missing = true; continue; }
    if (truth(scalar(read ? read.value(def, 'no_actions') : record(def.item_layer_types)?.no_actions ?? def.no_actions))) return true;
  }
  return missing ? null : false;
}
export interface DueTimedActivity { owner: Row; status: Row; family: TimedActivityFamily; outcome: 'due' | 'waiting' | 'handback' }
/** Roster order, then held target-row order. PHP SELECT has no ORDER BY: retain its returned order within each owner's held bucket. */
export function dueTimedActivities(rows: readonly Row[], clock: CampaignClock, ownerIds: readonly string[] | 'all'): DueTimedActivity[] {
  const owners = ownerIds === 'all' ? null : new Set(ownerIds.map(upper));
  const out: DueTimedActivity[] = [], seen = new Set<string>();
  for (const owner of rows) {
    if (owners && !owners.has(ecId(owner))) continue;
    for (const status of statusRows(owner, 'target')) {
      const family = timedActivityFamily(status), id = upper(status.status_id);
      if (!family || seen.has(id) || !timedActivityIsDue(status, clock)) continue;
      seen.add(id);
      const blocked = timedActivityOwnerBlocked(owner, id);
      out.push({ owner, status, family, outcome: blocked === true ? 'waiting' : blocked === null ? 'handback' : 'due' });
    }
  }
  return out;
}
export interface TimedFinishInput { encounterId: string; statusId: string; rollPrivate?: number }
export interface TimedFinishOutcome { outcome: 'completed' | 'refused' | 'waiting'; message?: string }

/** ft_campaign_context without fetching: nested stamp, a PC's attached campaign, else its encounter's campaign. */
export function timedFinishCampaignContext(row: Row, encounter: Row): boolean {
  if (record(row.campaign_arr) && Object.keys(record(row.campaign_arr)!).length) return true;
  if (UUID.test(upper(scalar(record(row.player_characters)?.attached_campaign_id)))) return true;
  const e = record(encounter.encounters) ?? encounter;
  return !!ecId(row) && upper(ecLean(row, 'encounter_id')) === upper(e.encounter_id)
    && String(scalar(e.encounter_master_parent_type) ?? '').toLowerCase() === 'campaign' && UUID.test(upper(scalar(e.encounter_master_parent_id)));
}

export function timedFinishSelection(status: Row, caster: Row, ability: Row, read: Pick<AbilityUseReaders, 'value' | 'inventoryField'> & Partial<Pick<AbilityUseReaders, 'placedAoe'>>): ReturnType<typeof parseTimedCastStorage> {
  const parsed = parseTimedCastStorage(status.status_casting_targets_arr);
  if (truth(read.value(ability, 'aoe_type')) && truth(read.value(ability, 'aoe_distance')) && upper(read.value(ability, 'range')) === SELF) {
    const shape = SHAPES[upper(read.value(ability, 'aoe_type'))];
    if (shape === 'line' || shape === 'cone') {
      const [eid, coords] = parsed.targetId.split(',')[0]!.split('|');
      const values = coords?.split(':').map(Number) ?? [];
      if (!UUID.test(upper(eid)) || values.length !== 6 || !values.every(Number.isFinite)) handBack('self-directional-aoe:aim-not-held');
      const span = placedAoeSpan(read.placedAoe ?? read, caster, ability, shape,
        { x: values[0]!, y: values[1]!, z: values[2]! }, { x: values[3]!, y: values[4]!, z: values[5]! });
      // Recompute occupants at finish; retain the saved aim and full length, including pre-edge casts.
      parsed.targetId = `${eid}|${[span.origin.x, span.origin.y, span.origin.z, span.endpoint.x, span.endpoint.y, span.endpoint.z].join(':')}`;
      return parsed;
    }
    const position = record(caster.position), at = position ?? record(caster.pos_arr);
    const eid = upper(ecLean(caster, 'encounter_id'));
    const x = scalar(at?.x_pos), y = scalar(at?.y_pos), z = scalar(at?.z_pos);
    // PHP's point builder returns '' without x/z; completion then retains the saved
    // selection. build_pos_arr elides zero axes in the current `position` projection.
    // Normalize full SQL-coordinate captures through that same sparse projection;
    // legacy pos_arr explicitly held zeros remain readable by PHP's tolerant reader.
    if (!UUID.test(eid) || x == null || x === '' || z == null || z === ''
        || (position && (Number(x) === 0 || Number(z) === 0))) return parsed;
    if (![x, y ?? 0, z].every(v => Number.isFinite(Number(v)))) handBack('timed-finish:position-not-held');
    parsed.targetId = `${eid}|${Number(x)}:${Number(y ?? 0)}:${Number(z)}`;
  }
  return parsed;
}

/** janus.php's single non-self creature soft veto, after finish precheck and target geometry,
 * before templates/dice. Multi-target areas retain their per-target post-effect checks. */
export function timedFinishCreatureTypeGate(ctx: JanusCtx, ability: Row, caster: Row, targets: Row[]): void {
  if (targets.length !== 1) return;
  const target = targets[0]!;
  if (!ecId(target) || ecId(target) === ecId(caster)
      || !['creature', 'player_character'].includes(String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? ''))) return;
  const checks = ['ability_works_only_against_creature_type', 'ability_does_not_work_against_creature_type'] as const;
  if (!checks.some(key => ctx.read.has(ability, key))) return;
  // Earlier PHP target predicates must win. Until their combined ordering is ported,
  // these overlapping contracts hand back rather than substitute a type refusal.
  if (record(ability.attack_ability_arr) || ['ability_only_affects_inventory', 'effect_only_affects_opposing_team_type',
    'effect_only_affects_same_team_type', 'ability_only_works_against_size_category_number_above',
    'ability_only_works_against_size_category_number_below', 'caster_dive_bombs_target'].some(key => ctx.read.has(ability, key))
      || targetImmuneToAbilityId(ctx, ability, target)
      || (['spell', 'curse'].includes(String(itemField(ability, 'item_type'))) && truth(ctx.read.value(target, 'immune_to_spells')))
      || hostileSpellImmunity(ctx, ability, caster, target)
      || (truth(ctx.read.value(target, 'immune_to_thought_reading')) && truth(ctx.read.value(ability, 'reads_target_thoughts'))))
    handBack('timed-finish:creature-type-gate-order');
  if (!record(target.item_layer_types)) handBack('timed-finish:creature-type-not-held');
  const ids = (row: Row, key: string): string[] => ctx.read.entries(row, key).map(entry => {
    const id = upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id'));
    if (!UUID.test(id)) handBack('timed-finish:malformed-creature-type');
    return id;
  });
  const targetTypes = ids(target, 'creature_type');
  for (const key of checks) {
    if (!ctx.read.has(ability, key)) continue;
    const restriction = ids(ability, key), matches = targetTypes.some(id => restriction.includes(id));
    if (key === 'ability_works_only_against_creature_type' ? matches : !matches) continue;
    const name = String(itemField(ability, 'item_name') ?? 'This ability');
    if (/[<&>]/.test(name)) handBack('timed-finish:creature-type-html-name');
    throw new Refusal(key === 'ability_works_only_against_creature_type'
      ? `${name} only works against certain creature types — this target does not qualify.`
      : `${name} does not work against this creature's type.`);
  }
}

function cleanup(ctx: JanusCtx, status: Row, caster: Row, owner: Row, logExpiry = true): void {
  const id = upper(status.status_id), item = statusItemId(status), target = ecId(owner);
  if (ctx.statusesRemoved.some(s => upper(s.target) === target && (upper(s.status_id) === id || (!s.status_id && upper(s.status_item_id) === item)))) return;
  const connection = upper(status.status_connected_id);
  if (item === TIMED_ACTIVITY_FAMILIES.casting_ritual_spell && connection
      && ctx.read.statuses(owner).some(s => upper(s.status_id) !== id && upper(s.status_connected_id) === connection
        && statusItemId(s) === 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B' && Number(s.status_deleted ?? 0) === 0)) {
    // The ritual START owns this concentration connection. Completion retires it through
    // the same narrow cascade as cancellation, after the finish has minted a fresh connection.
    resolveConnectionCascade(ctx, status, target);
    if (logExpiry) insertRoll(ctx, { roll_type_1: 'status_effect_expired', roll_item_id: item, by: caster, to: owner });
    return;
  }
  // The canonical removal also sweeps connected peers / lone concentration. Not all peers are page-visible; named residue.
  if (connection && ctx.read.statuses(owner).some(s => upper(s.status_id) !== id
      && upper(s.status_connected_id) === connection && Number(s.status_deleted ?? 0) === 0)) handBack('timed-finish:cleanup-connected');
  ctx.statusesRemoved.push({ status_id: id, status_item_id: item, target });
  const bucket = record(owner.statuses);
  if (bucket && Array.isArray(bucket.target)) bucket.target = bucket.target.filter(s => upper(record(s)?.status_id) !== id);
  ctx.statusConnectedId = null; // PHP loss-trigger dispatch creates a fresh connection.
  runTriggerFamily(ctx, { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: item }, owner, null, [owner], false, undefined, true);
  if (logExpiry && timedFinishCampaignContext(caster, ctx.encounter) && timedFinishCampaignContext(owner, ctx.encounter))
    insertRoll(ctx, { roll_type_1: 'status_effect_expired', roll_item_id: item, by: caster, to: owner });
}

/** ft_complete_timed_activity + normal cleanup, run on disposable ctx clones during every preflight. */
export function performTimedFinish(ctx: JanusCtx, input: TimedFinishInput, read: AbilityUseReaders, ordinaryLow = false): TimedFinishOutcome {
  if (upper(record(ctx.encounter.encounters)?.encounter_id ?? ctx.encounter.encounter_id) !== upper(input.encounterId)) handBack('timed-finish:encounter-not-held');
  const status = [...ctx.members.values()].flatMap(m => read.statuses(m)).find(s => upper(s.status_id) === upper(input.statusId));
  if (!status) handBack('timed-finish:status-not-held');
  const family = timedActivityFamily(status);
  if (!family) handBack('timed-finish:not-activity');
  const owner = ctx.members.get(upper(status.status_target_id)), caster = ctx.members.get(upper(status.status_caster_id));
  if (!owner || !caster) handBack('timed-finish:owner-not-held');
  if ([owner, caster].some(m => upper(ecLean(m, 'encounter_id')) !== upper(input.encounterId))) handBack('timed-finish:owner-outside-encounter');
  const clock = read.campaignClock();
  if (!clock || !timedActivityIsDue(status, clock)) handBack('timed-finish:not-due');
  const blocked = timedActivityOwnerBlocked(owner, input.statusId, read);
  if (blocked === true) return { outcome: 'waiting' };
  if (blocked === null) handBack('timed-finish:blocker-definition-not-held');
  if (family !== 'casting' && family !== 'casting_ritual_spell') {
    // Preserve the existing caller contract until the matching persistence capability is supplied.
    if (!(read as AbilityUseReaders & {timedFinishFamilyWrites?: boolean}).timedFinishFamilyWrites) handBack(`timed-finish:${family}`);
    const sourceId=upper(status.status_origin_id);
    const source=isSelfStatusOrigin(upper(status.status_id),sourceId) ? null : read.resolveInventory?.(sourceId) ?? read.definition(sourceId);
    if (!source) handBack(`timed-finish:${family}:source-not-held`);
    const gate=timedFinishGateVerdict({caster,ability:source,encounter:ctx.encounter,variable1:`finish_${family}`},read,input.statusId);
    if(gate.kind==='unknown') handBack(`timed-finish:${family}:precheck:${gate.site}`);
    if(gate.kind==='refuse') {cleanup(ctx,status,caster,owner);return {outcome:'refused',message:gate.message};}
    if (['gathering', 'disenchanting', 'lockpicking'].includes(family) && read.inventoryField?.(source, 'inventory_id'))
      ctx.timedInventory = { updates: [], creates: [], removals: [], logTargets: [upper(read.inventoryField(source, 'inventory_id'))] };
    // Capabilities are only supplied after the driver's optional persistence proposal is installed.
    const capabilities=read as AbilityUseReaders & {timedFinishFamilyWrites?: boolean;timedFinishReadingFacts?: (book:Row,reader:Row)=>ReadingFacts|undefined};
    if(family==='reading') {
      if(!capabilities.timedFinishFamilyWrites) handBack('timed-finish:reading:training-point-transport');
      finishReading(ctx,source,caster,capabilities.timedFinishReadingFacts?.(source,caster),()=>cleanup(ctx,status,caster,owner,false));
      return {outcome:'completed'};
    }
    if(family==='training') {
      if(!capabilities.timedFinishFamilyWrites) handBack('timed-finish:training:training-point-transport');
      finishTraining(ctx,status,owner,caster,()=>cleanup(ctx,status,caster,owner,false));
      return {outcome:'completed'};
    }
    if(family==='gathering' || family==='disenchanting') {
      if (ecId(owner) !== ecId(caster)) handBack(`timed-finish:${family}:owner-mismatch`);
      try {
        const finish = family === 'gathering' ? finishGathering : finishDisenchanting;
        finish(ctx, JSON.parse(JSON.stringify(source)) as Row, caster, () => cleanup(ctx,status,caster,owner,false), ordinaryLow);
      } catch (error) {
        if (!(error instanceof Refusal)) throw error;
        cleanup(ctx,status,caster,owner);
        return {outcome:'refused',message:error.refusal};
      }
      return {outcome:'completed'};
    }
    if(family==='lockpicking') {
      if(!capabilities.timedFinishFamilyWrites) handBack('timed-finish:lockpicking:row-binding-transport');
      const rows=read.statuses(caster).filter(s=>statusItemId(s)===TIMED_ACTIVITY_FAMILIES.lockpicking && Number(scalar(s.status_deleted)??0)===0);
      if(rows.length!==1 || upper(rows[0]!.status_id)!==upper(status.status_id)) handBack('timed-finish:lockpicking:ambiguous-attempt');
      cleanup(ctx,status,caster,owner,false);
      insertRoll(ctx,{roll_type_1:'failed_to_pick_lock',roll_item_id:sourceId,by:caster,to:source});
      return {outcome:'completed'};
    }
    handBack(`timed-finish:${family}`);
  }
  if (ecId(owner) !== ecId(caster)) handBack('timed-finish:casting-owner-mismatch');
  const abilityId = upper(status.status_origin_id), heldAbility = isSelfStatusOrigin(upper(status.status_id), abilityId) ? null : read.definition(abilityId);
  // An unheld source is not proof that the catalog source was deleted.
  if (!heldAbility) handBack('timed-finish:source-not-held');
  const stored = parseTimedCastStorage(status.status_casting_targets_arr);
  if (stored.consumeInventoryId !== undefined && !UUID.test(stored.consumeInventoryId)) handBack('timed-finish:consumable-source');
  if (stored.castLevel !== undefined && (!Number.isInteger(stored.castLevel) || stored.castLevel < 1 || stored.castLevel > 99))
    handBack('timed-finish:cast-level');
  const castLevel = family === 'casting' && !stored.freeCastMode ? stored.castLevel : undefined;
  const growth = upcastAreaGrowthFt(read, heldAbility, castLevel);
  const ability = growth > 0 ? { ...heldAbility, computed: { ...record(heldAbility.computed),
    aoe_distance: Number(read.value(heldAbility, 'aoe_distance') ?? 0) + growth } } : heldAbility;
  // The observer reads the cast's armed watches, but does not yet hydrate every watcher/lock/surge payload as the cast adapter does.
  // PHP finish_casting checks them at the finish: preserve that dispatch before any real die, resource debit or cleanup.
  if (family === 'casting' && String(itemField(ability, 'item_type')) === 'spell'
      && (read.counterspellWatches?.(ecId(caster))?.watches.length ?? 0) > 0
      // Wizard v9 spells_cannot_be_counterspelled: a protected finish is never examined (counterspellWatches returns none for it)
      && spellUncounterable(ctxProviderReaders(ctx), caster, ability) !== true) handBack('timed-finish:armed-counterspell-watch');
  const parsed = timedFinishSelection(status, caster, ability, read), ritual = family === 'casting_ritual_spell';
  const v1 = ritual ? 'finish_casting_ritual_spell' : 'finish_casting';
  const v2 = ritual ? UUID.test(upper(parsed.summonCreatureId)) ? upper(parsed.summonCreatureId) : null : parsed.freeCastMode || null;
  const charged = !ritual && ['charged_spell', 'charged_ability'].includes(String(v2));
  const source = charged && UUID.test(upper(parsed.freeCastSource)) ? read.resolveInventory?.(upper(parsed.freeCastSource)) ?? read.definition(upper(parsed.freeCastSource)) : null;
  if (charged && String(itemField(ability, 'item_type')) !== 'spell') handBack('timed-finish:charged-nonspell');
  if (charged && UUID.test(upper(parsed.freeCastSource)) && !source) handBack('timed-finish:charged-source-not-held');
  ctx.timedFinish = { statusId: upper(status.status_id), casterId: ecId(caster), abilityId, variable2Arr: source, summonPoints: parsed.summonPoints, castLevel };
  let message: string | undefined;
  if (source && Number(read.inventoryField?.(source, 'inventory_private') ?? 0) === 2) message = 'That item is merged into your current form and cannot be used until you revert.';
  const gate = message ? null : timedFinishGateVerdict({ caster, ability, encounter: ctx.encounter, variable1: v1, variable2: v2, castLevel,
    consumeInventoryId: stored.consumeInventoryId, consumeInventoryUnit: stored.consumeInventoryUnit,
    summonCreatureId: parsed.summonCreatureId }, read, ctx.timedFinish.statusId);
  if (gate?.kind === 'unknown') handBack(`timed-finish:precheck:${gate.site}`);
  if (gate?.kind === 'refuse') message = gate.message;
  if (!message && source && read.inventoryField?.(source, 'inventory_id')) {
    const current = read.value(source, 'current_item_charges');
    if (current != null && Number.isFinite(Number(current))) {
      const cost = -chargeReductionDelta(caster, -Math.trunc(Number(read.value(ability, v2 === 'charged_spell' ? 'spell_level' : 'ability_activation_charges') ?? 1)), resourceReductionReaders(ctx), source);
      if (Math.trunc(Number(current)) < cost) message = `You cannot use this ${iconPh(String(itemField(ability, 'item_type')))} because you have insufficient charges (${Math.trunc(Number(current))}) and it requires ${cost} charges.`;
    }
  }
  if (!message) {
    const janusInput: JanusInput = { casterId: ecId(caster), abilityId, targetIds: [], rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: v1, useAbilityVariable2: v2, castingTargets: parsed.targetId, selection: parsed.targetId,
      ...(stored.consumeInventoryId ? { consumeInventoryId: stored.consumeInventoryId,
        ...(stored.consumeInventoryUnit ? { consumeInventoryUnit: true as const } : {}) } : {}),
      summonCreatureId: parsed.summonCreatureId };
    const beforeRows = ctx.rows.length;
    try {
      const summoning = isSummonAbility(ctx, ability);
      if (parsed.summonPoints.length && !summoning) handBack('timed-finish:summon-points');
      let targets: Row[] = [];
      const targetRequired = (): string => 'This ' + String(itemField(ability, 'item_type') ?? 'ability').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') + ' has a range and requires a target; perhaps target the environment.';
      if (summoning) {
        ctx.summons = [];
        ctx.summonOrigin = { abilityId, casterId: ecId(caster) };
      }
      if (summoning && parsed.targetId.includes('|')) {
        if (!ctx.read.spawnSummons) handBack('timed-finish:summon-spawn-plan-not-held');
        const [eid, coordinates] = parsed.targetId.split('|');
        const axes = (coordinates ?? '').split(':').map(Number);
        const point = { x: axes[0] ?? NaN, y: axes.length === 2 ? 0 : axes[1] ?? NaN, z: axes.length === 2 ? axes[1] ?? NaN : axes[2] ?? NaN };
        if (upper(eid) !== upper(input.encounterId) || upper(eid) !== upper(ecLean(caster, 'encounter_id'))
            || ![2, 3].includes(axes.length) || !validSummonPoint(point)
            || parsed.summonPoints.length > SUMMON_POINTS_MAX || !parsed.summonPoints.every(validSummonPoint))
          handBack('timed-finish:summon-placement-invalid');
        ctx.summonRequest = { point, points: parsed.summonPoints, ...(parsed.summonCreatureId ? { pick: upper(parsed.summonCreatureId) } : {}) };
        targets = [summonPointTarget(upper(eid), point)];
      } else if (parsed.targetId.includes('|')) {
        if (!read.placedAoe) handBack('timed-finish:placement-not-held');
        // Wizard v9 aoe_spells_spare_allies: the expansion reads the cast-local skip-same-team stamp janus() also writes
        const expanded = finalizePlacedAoeTargets(read.placedAoe, { targetId: parsed.targetId }, caster,
          applyAoeSparesAllies(ctxProviderReaders(ctx), caster, ability, 'timed-finish'));
        targets = expanded.map(t => ctx.members.get(ecId(t)) ?? t);
      } else {
        for (const id of (upper(read.value(ability, 'range')) === SELF ? ecId(caster) : parsed.targetId).split(',').map(upper).filter(Boolean)) {
          const target = ctx.members.get(id);
          if (!target || Number(ecLean(target, 'encounter_creature_deleted') ?? 0) !== 0) { if (read.value(ability, 'range') == null) handBack('timed-finish:gone-rangeless-target'); throw new Refusal(targetRequired()); }
          targets.push(target);
        }
        if (upper(read.value(ability, 'range')) === SELF) targets = [caster];
        if (targets.length > 1) handBack('timed-finish:multi-target-validation');
        if (!targets.length && read.value(ability, 'range') != null && !fabricationAllowsTargetlessCast(ability, v1, read)) throw new Refusal(targetRequired());
        // The current range / sight facts are judged again, independent of the timed START's selection UI.
        for (const target of targets) {
          const failure = read.timedFinishTargetFailure?.(caster, target, ability);
          if (failure === undefined) handBack('timed-finish:target-geometry-not-held');
          if (failure) throw new Refusal(failure);
        }
      }
      timedFinishCreatureTypeGate(ctx, ability, caster, targets);
      // ft_complete_timed_activity → janus_invoke_for_timed_status_expiration(status_connected_id: new_guid()): the finish's effects get a
      // FRESH connection, never the expiring activity's own group (A12 Codex review P1) — null = mint one, as the engine-fired casts do
      ctx.statusConnectedId = null;
      janusInput.targetIds = targets.map(ecId);
      janus(ctx, janusInput, JSON.parse(JSON.stringify(ability)) as Row, caster, targets);
    } catch (error) {
      if (!(error instanceof Refusal)) throw error;
      if (ctx.rows.length !== beforeRows) handBack('timed-finish:refusal-after-resolution');
      message = error.refusal;
    }
  }
  cleanup(ctx, status, caster, owner);
  delete ctx.timedFinish;
  return message ? { outcome: 'refused', message } : { outcome: 'completed' };
}
